import {
  Dispatch,
  SetStateAction,
  useState,
  useCallback,
  useEffect,
} from 'react';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useDownloadApplication } from '../../../hooks';
import { CircularProgress, ListItemIcon } from '@mui/material';
import { useSnackbar } from 'notistack';
import Bugsnag from '@bugsnag/browser';
import { Quote } from '../../../Typescript/backend/classes';
import { graphql } from '../../../gql';
import { useLazyQuery } from '@apollo/client';
import { downloadPdfFile } from '@calefy-inc/utility';
import { useAuth } from '@calefy-inc/authentication';
import { SupportLink } from '../../common/SupportLink';
import { ErrorP } from '../../common/Typography';
import { styled } from '@mui/material';

interface PolicyStreamDownloadMenuButton {
  quote: Quote;
}
export const PolicyStreamFileDownloadMenuButton = ({
  quote,
}: PolicyStreamDownloadMenuButton) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  if (!quote.uniqueId) {
    return null;
  }
  return (
    <>
      <IconButton
        size='small'
        aria-label='download'
        onClick={(e) => {
          setMenuOpen(true);
          setAnchorEl(e.currentTarget);
        }}
      >
        <DownloadRoundedIcon fontSize='small' />
      </IconButton>
      <PolicyStreamFileDownloadMenu
        open={menuOpen}
        setOpen={setMenuOpen}
        quote={quote}
        anchorEl={anchorEl}
      />
    </>
  );
};

interface PolicyStreamFileDownloadMenuProps extends MenuProps {
  quote: Quote;
  setOpen: Dispatch<SetStateAction<boolean>>;
}
const PolicyStreamFileDownloadMenu = ({
  open,
  setOpen,
  quote,
  ...menuProps
}: PolicyStreamFileDownloadMenuProps) => {
  const [generatedError, setGeneratedError] = useState<Error>();
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false);
  const { token } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const handleApplicationFailure = useCallback(
    (e) => {
      console.error(e);
      Bugsnag.notify(e);
      enqueueSnackbar('Error downloading application document', {
        variant: 'error',
      });
      setGeneratedError(e);
      setOpen(false);
    },
    [enqueueSnackbar],
  );
  const [
    downloadComplianceDocument,
    // @ts-expect-error
    { loading: complianceLoading, error: complianceError },
  ] = useLazyQuery(DOWNLOAD_COMPLIANCE_DOCUMENT, {
    fetchPolicy: 'no-cache',
    onCompleted: async (data) => {
      if (data.downloadPolicystreamComplianceDocument) {
        await downloadPdfFile(
          data.downloadPolicystreamComplianceDocument,
          'Compliance Document.pdf',
        );
        enqueueSnackbar('Successfullly downloaded rating documents', {
          variant: 'success',
        });
        setOpen(false);
      } else {
        setGeneratedError(new Error('Empty document received'));
      }
    },
    onError: (e) => {
      console.error(e);
      Bugsnag.notify(e);
      enqueueSnackbar('Error downloading rating documents', {
        variant: 'error',
      });
      setGeneratedError(e);
    },
  });
  const [downloadApplicationDocuments, { loading: applicationLoading }] =
    useDownloadApplication({
      variables: { uuid: quote.uniqueId },
      onSuccess: (data) => {
        if (data.quotePdf?.pdfString) {
          enqueueSnackbar('Successfullly downloaded application document', {
            variant: 'success',
          });
          setOpen(false);
        } else {
          setGeneratedError(new Error(`Empty document generated`));
        }
      },
      onFailure: handleApplicationFailure,
    });

  // open the error dialog whenever we get an error
  useEffect(() => {
    if (generatedError) {
      setErrorDialogOpen(true);
    }
  }, [generatedError]);

  return (
    <>
      <Menu open={open} {...menuProps}>
        <ClickAwayListener
          onClickAway={() => {
            setOpen(false);
          }}
        >
          <MenuList>
            <MenuItem
              disabled={complianceLoading || applicationLoading}
              onClick={() => {
                downloadApplicationDocuments();
              }}
            >
              <ListItemIcon>
                {applicationLoading ? (
                  <CircularProgress size='1rem' />
                ) : (
                  <LinearScaleIcon />
                )}
              </ListItemIcon>
              <ListItemText>Application</ListItemText>
            </MenuItem>
            <MenuItem
              disabled={
                complianceLoading ||
                applicationLoading ||
                !token ||
                !quote.uniqueId
              }
              onClick={() => {
                downloadComplianceDocument({
                  variables: { token, uniqueId: quote.uniqueId },
                });
              }}
            >
              <ListItemIcon>
                {complianceLoading ? (
                  <CircularProgress size='1rem' />
                ) : (
                  <TrendingUpIcon />
                )}
              </ListItemIcon>
              <ListItemText>Compliance Documents</ListItemText>
            </MenuItem>
          </MenuList>
        </ClickAwayListener>
      </Menu>
      <DisplayErrorDialog
        open={errorDialogOpen}
        error={generatedError}
        close={() => setErrorDialogOpen(false)}
      />
    </>
  );
};

interface DisplayErrorDialogProps {
  open: boolean;
  error?: Error;
  close: () => void;
}
const DisplayErrorDialog = ({
  open,
  error,
  close,
}: DisplayErrorDialogProps) => {
  return (
    <Dialog open={open}>
      <DialogTitle>Unexpected Download Error</DialogTitle>
      <DialogContent>
        <Typography>
          Unfortunately we're having troubling downloading that form! You can
          try refreshing the page and trying again. If that doesn't work, please
          don't hesitate to reach out to our dedicated support team at{' '}
          <SupportLink
            subject={`Error downloading document at ${window.location}`}
          />
          .
        </Typography>
        <Typography>Our apologies for any inconvenience.</Typography>
        <ErrorP>Error: {error?.message || 'Unknown Error'}</ErrorP>
        <ErrorCloseButtonContainer>
          <Button onClick={() => close()}>OK</Button>
        </ErrorCloseButtonContainer>
      </DialogContent>
    </Dialog>
  );
};

const ErrorCloseButtonContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
});
const DOWNLOAD_COMPLIANCE_DOCUMENT = graphql(`
  query downloadPolicystreamComplianceDocument(
    $token: String!
    $uniqueId: ID!
  ) {
    downloadPolicystreamComplianceDocument(token: $token, uniqueId: $uniqueId)
  }
`);
